import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@arcadeai/design-system/components/ui/atoms/table";
import { cn } from "@arcadeai/design-system/lib/utils";
import { type Cell, flexRender } from "@tanstack/react-table";
import { memo } from "react";
import { useDataTable } from "./data-table";

type DataTableCellProps<TData> = {
  cell: Cell<TData, unknown>;
};

type DataTableContentProps = {
  stickyColumnBg?: "card" | "background";
};

// Get sticky classes for actions column
const getStickyClasses = (
  columnId: string,
  bgVariant: "card" | "background" = "background"
) => {
  if (columnId === "actions") {
    const bgClass = bgVariant === "card" ? "bg-card" : "bg-background";
    return `sticky right-0 ${bgClass} pl-6 pr-4 group-hover:bg-neutral-200 dark:group-hover:bg-neutral-700 transition-colors duration-200 z-50 [&>*]:pointer-events-auto [&>*]:relative [&>*]:z-50`;
  }
  return "";
};

const DataTableCell = memo(
  <TData,>({
    cell,
    stickyColumnBg,
  }: DataTableCellProps<TData> & {
    stickyColumnBg?: "card" | "background";
  }) => {
    const stickyClasses = getStickyClasses(cell.column.id, stickyColumnBg);

    return (
      <TableCell className={stickyClasses} key={cell.id}>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    );
  }
);

export function DataTableContent({
  stickyColumnBg = "background",
}: DataTableContentProps = {}) {
  const table = useDataTable();

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              className="group transition-colors duration-200 hover:bg-neutral-200 dark:hover:bg-neutral-700"
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => {
                const stickyClasses = getStickyClasses(
                  header.column.id,
                  stickyColumnBg
                );

                return (
                  <TableHead
                    className={cn(
                      stickyClasses,
                      stickyClasses && "rounded-tr-md"
                    )}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                className="group transition-colors duration-200 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                data-state={row.getIsSelected() && "selected"}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <DataTableCell
                    cell={cell}
                    key={cell.id}
                    stickyColumnBg={stickyColumnBg}
                  />
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className="h-24 text-center"
                colSpan={table.getAllColumns().length}
              >
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
